import { useEffect } from 'react';
import { useListContext } from 'react-admin';
import { FormControlLabel, Switch, Tooltip } from '@mui/material';
import { useCaretakerPreference } from '../fleet/myVehicles';

/**
 * „Tylko moje zlecenia” na liście banku (omówienie 01.10.2026).
 *
 * Moje = obsadzone auto albo aktywny wariant to auto, którego opiekunem FTL
 * jest zalogowany użytkownik. Front wysyła wyłącznie `myOrders: true` —
 * którym opiekunem jest pytający, API bierze z jego konta
 * (`User.ftlCaretaker`, `apps/api/src/user-offers/filters.ts`), nie z żądania.
 *
 * Wzór: „Tylko moje pojazdy” na liście floty (`fleet/FleetListOnlyMine.tsx`).
 * Filtr dla wygody, nie uprawnienie — jeden klik przywraca cały bank.
 * Pusta preferencja niczego nie ukrywa: przełącznik jest wtedy nieaktywny,
 * a zapamiętane „włączone” zdejmujemy (inaczej lista byłaby pusta bez
 * widocznego powodu).
 */

/** Klucz filtra — react-admin zapamiętuje go użytkownikom, nie zmieniamy. */
export const MY_ORDERS_FILTER = 'myOrders';

/**
 * Podpowiedź przy nieaktywnym przełączniku — mówi, co zrobić, żeby
 * zadziałał. Brzmienie jak przy „Tylko moje pojazdy” na liście floty
 * (poprawa brzmienia 01.10.2026).
 */
export const NO_CARETAKER_HINT =
  'Aby włączyć, wskaż siebie w polu „Jestem opiekunem FTL” w menu użytkownika (prawy górny róg).';

/**
 * Dymek przy aktywnym przełączniku — co znaczy „moje”. „Filtr widoku”, nie
 * „filtr dla wygody”, jak przy „Tylko moje pojazdy”.
 */
export const myOrdersHint = (caretaker: string) =>
  `Tylko zlecenia z pojazdami, których opiekunem FTL jest ${caretaker} — ` +
  'obsadzonymi albo wskazanymi w aktywnym wariancie. To filtr widoku, nie uprawnienie.';

const isOn = (value: unknown) => value === true || value === 'true';

export const MyOrdersToggle = () => {
  const { filterValues, displayedFilters, setFilters } = useListContext();
  const { caretaker, loaded } = useCaretakerPreference();
  const stored = filterValues?.[MY_ORDERS_FILTER];

  const withMine = (on: boolean) => {
    const next: Record<string, unknown> = { ...(filterValues ?? {}) };
    delete next[MY_ORDERS_FILTER];
    if (on) next[MY_ORDERS_FILTER] = true;
    return next;
  };

  useEffect(() => {
    if (loaded && caretaker === null && stored !== undefined) {
      setFilters(withMine(false), displayedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, caretaker, stored]);

  return (
    // `describeChild`: dymek opisuje przełącznik, nie zastępuje jego nazwy.
    <Tooltip describeChild title={caretaker === null ? NO_CARETAKER_HINT : myOrdersHint(caretaker)}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={caretaker !== null && isOn(stored)}
            disabled={caretaker === null}
            onChange={(e) => setFilters(withMine(e.target.checked), displayedFilters)}
          />
        }
        label="Tylko moje zlecenia"
      />
    </Tooltip>
  );
};
